<template>
	<layout-content-vertical-nav>
		<slot />

		<!-- Slot: Navbar -->
		<template #navbar="{ isVerticalNavMenuActive, toggleVerticalNavMenuActive }">
			<div
				class="navbar-content-container"
				:class="{ 'expanded-search': shallShowFullSearch }"
			>
				<!-- Left Content: Search -->
				<div class="d-flex align-center">
					<v-icon
						v-if="$vuetify.breakpoint.mdAndDown"
						class="me-3"
						@click="toggleVerticalNavMenuActive"
					>
						{{ icons.mdiMenu }}
					</v-icon>
					<app-bar-search
						:shall-show-full-search.sync="shallShowFullSearch"
						@update:shallShowFullSearch="
							handleShallShowFullSearchUpdate(isVerticalNavMenuActive, toggleVerticalNavMenuActive)
						"
					/>
				</div>

				<!-- Right Content: I18n, Light/Dark, Notification & User Dropdown -->
				<div class="d-flex align-center right-row">
					<meeting-today-widget />
					<v-badge :content="taskPendingCount > 99 ? '99+' : taskPendingCount" :value="taskPendingCount > 0" color="error" overlap>
						<v-btn icon small class="me-2 my-day-header-action" :aria-label="$t('AdminTasks.myDay')" :to="{ name: 'network-my-day' }">
						<v-icon>{{ icons.mdiCheckboxMarkedOutline }}</v-icon>
						</v-btn>
					</v-badge>
					<app-bar-content-publisher class="me-3" />
					<app-bar-i-g-d-b-import class="me-3" />
					<app-bar-pixel-grant class="me-3" />
					<app-bar-mail-hub class="me-4" />
					<app-bar-i18n class="me-2" />
					<app-bar-theme-switcher class="mx-3" />
					<app-bar-notification class="me-2" />
					<app-bar-user-menu />
				</div>
			</div>
		</template>

		<!-- Slot: Footer -->
		<template #footer>
			<div class="d-flex justify-space-between">
				<span>COPYRIGHT &copy; {{ new Date().getFullYear() }}
					<a
						href="https://mundogamer.network/"
						class="text-decoration-none"
					>Mundo Gamer Network</a>, All
					rights reserved</span>
			</div>
		</template>

		<template #v-app-content>
			<!-- <app-customizer class="d-none d-md-block"></app-customizer> -->
		</template>

		<!-- Payout alert snackbar — shown on WebSocket event -->
		<v-snackbar
			v-model="payoutSnackbar.show"
			color="warning"
			:timeout="12000"
			top
			right
			multi-line
		>
			<div>
				<div class="font-weight-bold">
					💸 Saque solicitado
				</div>
				<div class="text-body-2 mt-1">
					{{ payoutSnackbar.message }}
				</div>
			</div>
			<template #action>
				<v-btn
					text
					@click="goToPayouts"
				>
					Ver saques
				</v-btn>
				<v-btn
					icon
					small
					@click="payoutSnackbar.show = false"
				>
					<v-icon small>
						mdi-close
					</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
	</layout-content-vertical-nav>
</template>

<script>
import LayoutContentVerticalNav from '@/@core/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import AppCustomizer from '@core/layouts/components/app-customizer/AppCustomizer.vue'

// App Bar Components
import AppBarI18n from '@core/layouts/components/app-bar/AppBarI18n.vue'
import AppBarContentPublisher from '@core/layouts/components/app-bar/AppBarContentPublisher.vue'
import AppBarIGDBImport from '@core/layouts/components/app-bar/AppBarIGDBImport.vue'
import AppBarMailHub from '@core/layouts/components/app-bar/AppBarMailHub.vue'
import AppBarPixelGrant from '@core/layouts/components/app-bar/AppBarPixelGrant.vue'
import AppBarNotification from '@core/layouts/components/app-bar/AppBarNotification.vue'
import AppBarSearch from '@core/layouts/components/app-bar/AppBarSearch.vue'
import AppBarThemeSwitcher from '@core/layouts/components/app-bar/AppBarThemeSwitcher.vue'
import AppBarUserMenu from '@core/layouts/components/app-bar/AppBarUserMenu.vue'
import MeetingTodayWidget from '@core/layouts/components/app-bar/MeetingTodayWidget.vue'
import { mdiCheckboxMarkedOutline, mdiHeartOutline, mdiMenu } from '@mdi/js'
import { getPersonalTaskSummary } from '@/services/admin-personal-tasks.service'

import { getVuetify } from '@core/utils'

import { ref } from 'vue'

export default {
	components: {
		LayoutContentVerticalNav,
		AppCustomizer,

		// App Bar Components
		AppBarSearch,
		AppBarI18n,
		AppBarContentPublisher,
		AppBarIGDBImport,
		AppBarThemeSwitcher,
		AppBarUserMenu,
		AppBarMailHub,
		AppBarNotification,
		AppBarPixelGrant,
		MeetingTodayWidget,
	},
	setup() {
		const $vuetify = getVuetify()

		// Search
		const shallShowFullSearch = ref(false)

		// ? Handles case where in <lg vertical nav menu is open and search is triggered using hotkey then searchbox is hidden behind vertical nav menu overlaty
		const handleShallShowFullSearchUpdate = (isVerticalNavMenuActive, toggleVerticalNavMenuActive) => {
			if ($vuetify.breakpoint.mdAndDown && isVerticalNavMenuActive) {
				toggleVerticalNavMenuActive()
			}
		}

		return {
			handleShallShowFullSearchUpdate,
			shallShowFullSearch,

			icons: {
				mdiMenu,
				mdiCheckboxMarkedOutline,
				mdiHeartOutline,
			},
		}
	},
	data() {
		return {
			payoutSnackbar: { show: false, message: '' },
			taskPendingCount: 0,
		}
	},
	mounted() {
		this.loadTaskPendingCount()
		this.taskPendingTimer = setInterval(this.loadTaskPendingCount, 60000)
		this.$_indieWallPayoutChannel = null
		this.$_subscribePayoutChannel()
	},
	beforeDestroy() {
		clearInterval(this.taskPendingTimer)
		this.$_unsubscribePayoutChannel()
	},
	methods: {
		async loadTaskPendingCount() {
			try {
				const response = await getPersonalTaskSummary()
				this.taskPendingCount = Number(response.data?.data?.open || 0)
			} catch (e) { this.taskPendingCount = 0 }
		},
		$_subscribePayoutChannel() {
			const echo = this.$echo || window.Echo
			if (!echo) return
			try {
				this.$_indieWallPayoutChannel = echo.channel('indie-wall-admin')
				this.$_indieWallPayoutChannel.listen('.payout.requested', (payload) => {
					const creator = payload.creator_name || 'Unknown'
					const currency = payload.currency || 'USD'
					const amount = Number(payload.net_amount || 0).toFixed(2)
					this.payoutSnackbar.message = `${creator} · ${currency} ${amount}`
					this.payoutSnackbar.show = true
				})
			} catch (e) {
				// Non-fatal if WebSocket is unavailable
			}
		},
		$_unsubscribePayoutChannel() {
			try {
				const echo = this.$echo || window.Echo
				if (echo) echo.leave('indie-wall-admin')
			} catch (e) { /* ignore */ }
			this.$_indieWallPayoutChannel = null
		},
		goToPayouts() {
			this.payoutSnackbar.show = false
			if (this.$route.name !== 'tv-walls-payouts') {
				this.$router.push({ name: 'tv-walls-payouts' }).catch(() => {})
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
	height: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-grow: 1;
	position: relative;
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
	.expanded-search {
		::v-deep .app-bar-autocomplete-box div[role='combobox'] {
			background-color: transparent;
		}

		> .d-flex > button.v-icon {
			display: none;
		}

		// ===

		& > .right-row {
			visibility: hidden;
			opacity: 0;
		}

		::v-deep .app-bar-search-toggler {
			visibility: hidden;
		}
	}
}
</style>

<style lang="scss" scoped>
.my-day-header-action {
  background: transparent !important;
  box-shadow: none !important;
}
.my-day-header-action:hover,
.my-day-header-action:focus {
  background: rgba(255, 255, 255, 0.08) !important;
}
</style>
